import React, { useState, useEffect, useRef } from 'react';
import FileStorageStats from './FileStorageStats';
import { Button, Input, Modal, Loader, Alert } from './shared';

const FileManager = ({ service, isOnline }) => {
  const [currentPath, setCurrentPath] = useState('');
  const [folderContents, setFolderContents] = useState({ items: [], pagination: {} });
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('grid'); // 'grid' or 'list'
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [folderTree, setFolderTree] = useState(null);
  const [expandedFolders, setExpandedFolders] = useState(new Set(['']));
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [createFolderDialog, setCreateFolderDialog] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [activeTab, setActiveTab] = useState('files'); // 'files' or 'stats'
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [renameDialog, setRenameDialog] = useState(false);
  const [renameItem, setRenameItem] = useState(null);
  const [newItemName, setNewItemName] = useState('');

  // File type icons
  const getFileIcon = (fileName, type) => {
    if (type === 'folder') {
      return <i className="fas fa-folder" style={{ color: '#000000' }}></i>;
    }

    const extension = fileName.split('.').pop()?.toLowerCase();
    const iconMap = {
      'pdf': <i className="fas fa-file-pdf" style={{ color: '#000000' }}></i>,
      'doc': <i className="fas fa-file-word" style={{ color: '#000000' }}></i>,
      'docx': <i className="fas fa-file-word" style={{ color: '#000000' }}></i>,
      'xls': <i className="fas fa-file-excel" style={{ color: '#000000' }}></i>,
      'xlsx': <i className="fas fa-file-excel" style={{ color: '#000000' }}></i>,
      'ppt': <i className="fas fa-file-powerpoint" style={{ color: '#000000' }}></i>,
      'pptx': <i className="fas fa-file-powerpoint" style={{ color: '#000000' }}></i>,
      'txt': <i className="fas fa-file-alt" style={{ color: '#000000' }}></i>,
      'jpg': <i className="fas fa-file-image" style={{ color: '#000000' }}></i>,
      'jpeg': <i className="fas fa-file-image" style={{ color: '#000000' }}></i>,
      'png': <i className="fas fa-file-image" style={{ color: '#000000' }}></i>,
      'gif': <i className="fas fa-file-image" style={{ color: '#000000' }}></i>,
      'svg': <i className="fas fa-file-image" style={{ color: '#000000' }}></i>,
      'mp4': <i className="fas fa-file-video" style={{ color: '#000000' }}></i>,
      'avi': <i className="fas fa-file-video" style={{ color: '#000000' }}></i>,
      'mov': <i className="fas fa-file-video" style={{ color: '#000000' }}></i>,
      'webm': <i className="fas fa-file-video" style={{ color: '#000000' }}></i>,
      'mp3': <i className="fas fa-file-audio" style={{ color: '#000000' }}></i>,
      'wav': <i className="fas fa-file-audio" style={{ color: '#000000' }}></i>,
      'flac': <i className="fas fa-file-audio" style={{ color: '#000000' }}></i>,
      'ogg': <i className="fas fa-file-audio" style={{ color: '#000000' }}></i>,
      'zip': <i className="fas fa-file-archive" style={{ color: '#000000' }}></i>,
      'rar': <i className="fas fa-file-archive" style={{ color: '#000000' }}></i>,
      '7z': <i className="fas fa-file-archive" style={{ color: '#000000' }}></i>,
      'js': <i className="fas fa-file-code" style={{ color: '#000000' }}></i>,
      'html': <i className="fas fa-file-code" style={{ color: '#000000' }}></i>,
      'css': <i className="fas fa-file-code" style={{ color: '#000000' }}></i>,
      'py': <i className="fas fa-file-code" style={{ color: '#000000' }}></i>
    };

    return iconMap[extension] || <i className="fas fa-file" style={{ color: '#000000' }}></i>;
  };

  // Load folder contents
  const loadFolderContents = async (path = currentPath, page = 1) => {
    if (!isOnline) return;

    setLoading(true);
    setError(null);

    try {
      // For empty path (root), use empty string which the backend handles correctly
      const folderPath = path || '';
      const url = `${service.url}/api/storage/folders/${folderPath}/contents?page=${page}&page_size=50`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      setFolderContents(data);
      setSelectedItems(new Set());
      
    } catch (err) {
      console.error('Error loading folder contents:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load folder tree
  const loadFolderTree = async () => {
    if (!isOnline) return;

    try {
      const response = await fetch(`${service.url}/api/storage/folders/tree`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        const tree = await response.json();
        setFolderTree(tree);
      }
    } catch (err) {
      console.error('Error loading folder tree:', err);
    }
  };

  // Initial load
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isOnline) {
      loadFolderContents();
      loadFolderTree();
    }
  }, [isOnline, currentPath]);

  // Handle folder navigation
  const navigateToFolder = (path) => {
    setCurrentPath(path);
    setShowSearch(false);
    setSearchQuery('');
  };

  // Generate breadcrumbs
  const getBreadcrumbs = () => {
    if (!currentPath) return [{ name: 'Root', path: '' }];
    
    const parts = currentPath.split('/');
    const breadcrumbs = [{ name: 'Root', path: '' }];
    
    let buildPath = '';
    for (const part of parts) {
      buildPath = buildPath ? `${buildPath}/${part}` : part;
      breadcrumbs.push({ name: part, path: buildPath });
    }
    
    return breadcrumbs;
  };

  // Handle file upload
  const handleFileUpload = async (files) => {
    if (!files.length || !isOnline) return;
    
    setUploading(true);
    const newProgress = {};
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      newProgress[file.name] = 0;
      setUploadProgress({...newProgress});
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await fetch(`${service.url}/api/storage/files/upload/${currentPath}`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${service.apiKey}`
          },
          body: formData
        });
        
        if (!response.ok) {
          throw new Error(`Upload failed: ${response.statusText}`);
        }
        
        newProgress[file.name] = 100;
        setUploadProgress({...newProgress});
        
      } catch (err) {
        console.error(`Error uploading ${file.name}:`, err);
        setError(`Failed to upload ${file.name}: ${err.message}`);
      }
    }
    
    setUploading(false);
    setUploadProgress({});
    setShowUpload(false);
    loadFolderContents(); // Refresh
  };

  // Handle drag and drop
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  // Handle item selection
  const toggleItemSelection = (itemPath) => {
    const newSelected = new Set(selectedItems);
    if (newSelected.has(itemPath)) {
      newSelected.delete(itemPath);
    } else {
      newSelected.add(itemPath);
    }
    setSelectedItems(newSelected);
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchQuery.trim() || !isOnline) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        query: searchQuery,
        folder_path: currentPath
      });
      
      const response = await fetch(`${service.url}/api/storage/files/search?${params}`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.results);
        setShowSearch(true);
      }
    } catch (err) {
      setError('Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Create folder
  const createFolder = async () => {
    if (!newFolderName.trim() || !isOnline) return;
    
    try {
      const folderPath = currentPath ? `${currentPath}/${newFolderName}` : newFolderName;
      const response = await fetch(`${service.url}/api/storage/folders/${folderPath}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        setCreateFolderDialog(false);
        setNewFolderName('');
        loadFolderContents();
        loadFolderTree();
      }
    } catch (err) {
      setError('Failed to create folder');
    }
  };

  // Delete selected items
  const deleteSelectedItems = async () => {
    if (selectedItems.size === 0) return;
    
    setDeleteConfirmDialog(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmDialog(false);
    
    for (const itemPath of selectedItems) {
      try {
        await fetch(`${service.url}/api/storage/files/${itemPath}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${service.apiKey}`
          }
        });
      } catch (err) {
        console.error(`Failed to delete ${itemPath}:`, err);
      }
    }
    
    setSelectedItems(new Set());
    loadFolderContents();
    loadFolderTree();
  };

  // Get signed URL for file
  const getFileUrl = async (filePath) => {
    try {
      const response = await fetch(`${service.url}/api/storage/url/${encodeURIComponent(filePath)}`, {
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.download_url;
      }
    } catch (err) {
      console.error('Failed to get file URL:', err);
    }
    return null;
  };

  // Download file
  const downloadFile = async (filePath, fileName) => {
    try {
      const url = await getFileUrl(filePath);
      if (url) {
        // Create a temporary anchor element and click it to download
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || filePath.split('/').pop();
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (err) {
      console.error('Failed to download file:', err);
    }
  };

  // Context menu handlers
  const handleContextMenu = (e, item) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      item: item
    });
  };

  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => closeContextMenu();
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  // Rename item
  const handleRenameClick = (item) => {
    setRenameItem(item);
    setNewItemName(item.name);
    setRenameDialog(true);
    closeContextMenu();
  };

  const handleRename = async () => {
    if (!renameItem || !newItemName || newItemName === renameItem.name) {
      setRenameDialog(false);
      return;
    }

    try {
      // For folders, use folder move endpoint
      // For files, use file move endpoint
      // Since the API doesn't have a dedicated rename, we'd use move to rename
      // For now, show a message that this feature needs API support
      alert('Rename functionality requires API endpoint implementation');
      setRenameDialog(false);
      setRenameItem(null);
      setNewItemName('');
    } catch (err) {
      console.error('Failed to rename:', err);
      alert('Failed to rename item');
    }
  };

  // Delete item
  const handleDeleteClick = (item) => {
    setRenameItem(item);
    setDeleteConfirmDialog(true);
    closeContextMenu();
  };

  const handleDelete = async () => {
    if (!renameItem) return;

    try {
      const endpoint = renameItem.type === 'folder'
        ? `/api/storage/folders/${renameItem.path}?force=true`
        : `/api/storage/files/${renameItem.path}`;

      const response = await fetch(`${service.url}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${service.apiKey}`
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      // Close dialog first
      setDeleteConfirmDialog(false);
      setRenameItem(null);

      // Reload folder contents (don't await to avoid blocking dialog close)
      loadFolderContents().catch(err => console.error('Error reloading contents:', err));
      loadFolderTree().catch(err => console.error('Error reloading tree:', err));
    } catch (err) {
      console.error('Failed to delete:', err);
      alert('Failed to delete item: ' + err.message);
    }
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString();
  };

  // Render folder tree node
  const renderFolderTreeNode = (node, level = 0) => {
    const isExpanded = expandedFolders.has(node.path);
    const isSelected = currentPath === node.path;
    
    return (
      <div key={node.path} className={`folder-tree-node level-${level}`}>
        <div 
          className={`flex items-center py-1 px-2 cursor-pointer hover:bg-gray-100 ${
            isSelected ? 'bg-[#FFC65D] bg-opacity-20' : ''
          }`}
          style={{ paddingLeft: `${level * 16 + 8}px` }}
          onClick={() => navigateToFolder(node.path)}
        >
          {node.children && node.children.length > 0 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                const newExpanded = new Set(expandedFolders);
                if (isExpanded) {
                  newExpanded.delete(node.path);
                } else {
                  newExpanded.add(node.path);
                }
                setExpandedFolders(newExpanded);
              }}
              className="w-4 h-4 mr-1 flex items-center justify-center"
            >
              {isExpanded ? '▼' : '▶'}
            </button>
          )}
          <i className="fas fa-folder" style={{ marginRight: '8px', color: '#000000' }}></i>
          <span className="text-sm">{node.name}</span>
        </div>
        
        {isExpanded && node.children && (
          <div>
            {node.children.map(child => renderFolderTreeNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  if (!isOnline) {
    return (
      <Alert variant="error" title="File Storage Service Offline">
        The file storage service is currently unavailable.
      </Alert>
    );
  }

  return (
    <div className="file-manager h-full flex">
      {/* Sidebar - Folder Tree */}
      <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <div className="flex space-x-2 mb-3">
            <button
              onClick={() => setActiveTab('files')}
              className={`px-3 py-1 text-sm rounded ${
                activeTab === 'files'
                  ? 'bg-[#FFC65D] text-black'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Files
            </button>
            <button
              onClick={() => setActiveTab('stats')}
              className={`px-3 py-1 text-sm rounded ${
                activeTab === 'stats'
                  ? 'bg-[#FFC65D] text-black'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Stats
            </button>
          </div>
          {activeTab === 'files' && <h3 className="font-medium text-gray-900">Folders</h3>}
        </div>
        <div className="flex-1 overflow-auto">
          {activeTab === 'files' && folderTree && renderFolderTreeNode(folderTree)}
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col">
        {/* Toolbar */}
        <div className="bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          {/* Breadcrumbs */}
          <div className="flex items-center space-x-2">
            {getBreadcrumbs().map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && <span className="text-gray-400">/</span>}
                <button
                  onClick={() => navigateToFolder(crumb.path)}
                  className="text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80"
                >
                  {crumb.name}
                </button>
              </React.Fragment>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2">
            {/* Search */}
            <div className="flex items-center space-x-2">
              <Input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button variant="primary" size="sm" onClick={handleSearch}>
                Search
              </Button>
            </div>

            {/* View Toggle */}
            <div className="flex border border-gray-300 rounded">
              <button
                onClick={() => setView('grid')}
                className={`px-3 py-1 text-sm ${view === 'grid' ? 'bg-[#FFC65D] text-black' : 'hover:bg-gray-100'}`}
              >
                Grid
              </button>
              <button
                onClick={() => setView('list')}
                className={`px-3 py-1 text-sm ${view === 'list' ? 'bg-[#FFC65D] text-black' : 'hover:bg-gray-100'}`}
              >
                List
              </button>
            </div>

            {/* Upload Button */}
            <Button variant="success" onClick={() => setShowUpload(true)}>
              Upload
            </Button>

            {/* Create Folder */}
            <Button variant="secondary" onClick={() => setCreateFolderDialog(true)}>
              New Folder
            </Button>

            {/* Delete Selected */}
            {selectedItems.size > 0 && (
              <Button variant="danger" onClick={deleteSelectedItems}>
                Delete ({selectedItems.size})
              </Button>
            )}
          </div>
        </div>

        {/* Content Area */}
        <div
          className={`flex-1 ${activeTab === 'files' ? 'p-4' : ''} ${dragOver ? 'bg-gray-50 border-2 border-dashed border-gray-300' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          {/* Show stats tab */}
          {activeTab === 'stats' && (
            <div className="p-6">
              <FileStorageStats service={service} isOnline={isOnline} />
            </div>
          )}

          {/* Show files tab */}
          {activeTab === 'files' && (
            <div>
          {loading && (
            <div className="flex items-center justify-center h-32">
              <Loader size="md" />
            </div>
          )}

          {error && (
            <Alert variant="error">
              {error}
            </Alert>
          )}

          {/* Search Results */}
          {showSearch && (
            <div className="mb-4">
              <h3 className="font-medium mb-2">Search Results for "{searchQuery}"</h3>
              <div className="space-y-2">
                {searchResults.map(file => (
                  <div key={file.path} className="flex items-center p-2 border rounded hover:bg-gray-50">
                    <span className="mr-3">{getFileIcon(file.name, file.type)}</span>
                    <div className="flex-1">
                      <div className="font-medium">{file.name}</div>
                      <div className="text-sm text-gray-500">{file.folder || 'Root'}</div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {formatFileSize(file.size)} • {formatDate(file.modified)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* File Grid/List */}
          {!loading && !showSearch && (
            <div className={view === 'grid' ? 'grid grid-cols-6 gap-4' : 'space-y-2'}>
              {folderContents.items.map(item => (
                <div
                  key={item.path}
                  className={`
                    ${view === 'grid'
                      ? 'p-4 border rounded hover:bg-gray-50 cursor-pointer text-center'
                      : 'flex items-center p-2 border rounded hover:bg-gray-50 cursor-pointer'
                    }
                    ${selectedItems.has(item.path) ? 'bg-[#FFC65D] bg-opacity-20 border-[#FFC65D]' : ''}
                  `}
                  onClick={() => {
                    if (item.type === 'folder') {
                      navigateToFolder(item.path);
                    } else {
                      toggleItemSelection(item.path);
                    }
                  }}
                  onDoubleClick={async () => {
                    if (item.type === 'file') {
                      const url = await getFileUrl(item.path);
                      if (url) {
                        window.open(url, '_blank');
                      }
                    }
                  }}
                  onContextMenu={(e) => handleContextMenu(e, item)}
                >
                  {view === 'grid' ? (
                    <>
                      <div className="text-4xl mb-2">{getFileIcon(item.name, item.type)}</div>
                      <div className="text-sm font-medium truncate">{item.name}</div>
                      {item.type === 'file' && (
                        <>
                          <div className="text-xs text-gray-500">{formatFileSize(item.size)}</div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFile(item.path, item.name);
                            }}
                            className="mt-2 text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80 text-xs px-2 py-1 rounded hover:bg-[#FFC65D] hover:bg-opacity-10"
                            title={`Download ${item.name}`}
                          >
                            <i className="fas fa-download" style={{ marginRight: '4px', color: '#000000' }}></i>Download
                          </button>
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.path)}
                        onChange={() => toggleItemSelection(item.path)}
                        className="mr-3"
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="mr-3 text-xl">{getFileIcon(item.name, item.type)}</span>
                      <div className="flex-1">
                        <div className="font-medium">{item.name}</div>
                      </div>
                      <div className="text-sm text-gray-500 w-20">
                        {item.type === 'file' ? formatFileSize(item.size) : 'Folder'}
                      </div>
                      <div className="text-sm text-gray-500 w-24">
                        {formatDate(item.modified)}
                      </div>
                      {item.type === 'file' && (
                        <div className="ml-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadFile(item.path, item.name);
                            }}
                            className="text-[#FFC65D] hover:text-[#FFC65D] hover:opacity-80 text-sm px-2 py-1 rounded hover:bg-[#FFC65D] hover:bg-opacity-10"
                            title={`Download ${item.name}`}
                          >
                            <i className="fas fa-download" style={{ marginRight: '4px', color: '#000000' }}></i>Download
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Empty State */}
          {!loading && !showSearch && folderContents.items.length === 0 && (
            <div className="text-center py-12">
              <div className="text-4xl mb-4"><i className="fas fa-folder-open" style={{ fontSize: '48px', color: '#000000' }}></i></div>
              <p className="text-gray-500">This folder is empty</p>
              <Button variant="primary" onClick={() => setShowUpload(true)} className="mt-4">
                Upload Files
              </Button>
            </div>
          )}
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <Modal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        title="Upload Files"
        size="sm"
        footer={
          <div className="flex justify-end">
            <Button
              variant="secondary"
              onClick={() => setShowUpload(false)}
              disabled={uploading}
            >
              Close
            </Button>
          </div>
        }
      >
        <div className="mb-4">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFileUpload(Array.from(e.target.files))}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="w-full p-8 border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-[#FFC65D]"
          >
            Click to select files or drag and drop
          </button>
        </div>

        {uploading && (
          <div>
            <p className="text-sm text-gray-600 mb-2">Uploading files...</p>
            {Object.entries(uploadProgress).map(([filename, progress]) => (
              <div key={filename} className="mb-2">
                <div className="text-sm">{filename}</div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-[#FFC65D] h-2 rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* Create Folder Dialog */}
      <Modal
        isOpen={createFolderDialog}
        onClose={() => setCreateFolderDialog(false)}
        title="Create New Folder"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setCreateFolderDialog(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={createFolder}>
              Create
            </Button>
          </div>
        }
      >
        <Input
          type="text"
          placeholder="Folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && createFolder()}
          fullWidth
        />
      </Modal>

      {/* Delete Confirmation Dialog */}
      <Modal
        isOpen={deleteConfirmDialog}
        onClose={() => {
          setDeleteConfirmDialog(false);
          setRenameItem(null);
        }}
        title="Confirm Delete"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => {
              setDeleteConfirmDialog(false);
              setRenameItem(null);
            }}>
              Cancel
            </Button>
            <Button variant="danger" onClick={renameItem ? handleDelete : confirmDelete}>
              Delete
            </Button>
          </div>
        }
      >
        <p className="text-gray-700">
          {renameItem
            ? `Are you sure you want to delete "${renameItem.name}"? This action cannot be undone.`
            : `Are you sure you want to delete ${selectedItems.size} item(s)? This action cannot be undone.`
          }
        </p>
      </Modal>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-white border shadow-lg rounded-lg py-2 z-50"
          style={{ top: contextMenu.y, left: contextMenu.x }}
        >
          <button
            onClick={() => handleRenameClick(contextMenu.item)}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center"
          >
            <i className="fas fa-edit" style={{ marginRight: '8px', color: '#000000' }}></i>Rename
          </button>
          <button
            onClick={() => handleDeleteClick(contextMenu.item)}
            className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center text-red-600"
          >
            <i className="fas fa-trash" style={{ marginRight: '8px', color: '#dc2626' }}></i>Delete
          </button>
        </div>
      )}

      {/* Rename Dialog */}
      <Modal
        isOpen={renameDialog && renameItem !== null}
        onClose={() => {
          setRenameDialog(false);
          setRenameItem(null);
          setNewItemName('');
        }}
        title={`Rename ${renameItem?.type === 'folder' ? 'Folder' : 'File'}`}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => {
              setRenameDialog(false);
              setRenameItem(null);
              setNewItemName('');
            }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleRename}>
              Rename
            </Button>
          </div>
        }
      >
        <Input
          type="text"
          label="New name"
          value={newItemName}
          onChange={(e) => setNewItemName(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              handleRename();
            }
          }}
          autoFocus
          fullWidth
        />
      </Modal>
    </div>
  );
};

export default FileManager;